import React from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Box, Text, Icon, useBreakpointValue } from "@chakra-ui/react";
import { MdLocationOn, MdEvent, MdAccessTime, MdSchedule, MdPayment, MdReceipt, MdCelebration } from "react-icons/md";
import ContactInfoRow from "./shared/ContactInfoRow";
import getStatusColor from "./shared/getStatusColor";
import { currencyByAddress } from "../../../tools/tools";
import { buildMapsUrl, formatCoordinates, hasCoordinates } from "../../../tools/geo";
import { eventTypeLabel } from "constants/eventTypeLabels";

/**
 * Formats the artist arrival time with a "before" prefix.
 * @param {Object} reservation - The reservation data object
 * @returns {React.ReactElement} Formatted arrival time element
 */
function formatArrivalTime(reservation) {
    return (
        <Box as="span">
            <Text as="span" fontSize="0.7rem" color="rgba(255, 255, 255, 0.5)">
                avant -&nbsp;
            </Text>
            {dayjs(reservation?.service_time_to_arrived).format("DD-MM-YYYY a HH:mm")}
        </Box>
    );
}

/**
 * Formats the service end time with an "after" prefix.
 * @param {Object} reservation - The reservation data object
 * @returns {React.ReactElement} Formatted end time element
 */
function formatEndTime(reservation) {
    return (
        <Box as="span">
            <Text as="span" fontSize="0.7rem" color="rgba(255, 255, 255, 0.5)">
                apres -&nbsp;
            </Text>
            {dayjs(reservation?.service_time_to_finished).format("DD-MM-YYYY a HH:mm")}
        </Box>
    );
}

/**
 * Renders the invoice and status rows at the top of the reservation details.
 * @param {Object} props
 * @param {Object} props.reservation - The reservation data object
 * @param {boolean} props.invoiceCopied - Whether the invoice was just copied
 * @param {Function} props.onCopyInvoice - Handler to copy the invoice
 * @param {Object} props.statusColors - Color scheme for the status chip
 * @param {string} props.statusLabel - Translated status label
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ReservationHeaderRows({ reservation, invoiceCopied, onCopyInvoice, statusColors, statusLabel, isMobile }) {
    return (
        <>
            {reservation?.invoice && (
                <ContactInfoRow
                    icon={<Icon as={MdReceipt} />}
                    label="Facture"
                    value={reservation.invoice.substring(0, 10) + "..."}
                    copyable isCopied={invoiceCopied}
                    onCopy={onCopyInvoice} isMobile={isMobile}
                />
            )}
            <ContactInfoRow
                label="Statut" value={statusLabel}
                chip chipColor={statusColors} isMobile={isMobile}
            />
        </>
    );
}

/**
 * Renders the event detail rows (address, event, dates, times).
 * @param {Object} props
 * @param {Object} props.reservation - The reservation data object
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ReservationEventRows({ reservation, isMobile }) {
    return (
        <>
            <ContactInfoRow
                icon={<Icon as={MdLocationOn} />}
                label="Adresse" value={reservation?.address} isMobile={isMobile}
            />
            {hasCoordinates(reservation) && (
                <ContactInfoRow
                    icon={<Icon as={MdLocationOn} />}
                    label="Coordonnees GPS"
                    value={formatCoordinates(reservation.latitude, reservation.longitude)}
                    isInvoice invoiceUrl={buildMapsUrl(reservation.latitude, reservation.longitude)}
                    isMobile={isMobile}
                />
            )}
            <ContactInfoRow
                icon={<Icon as={MdCelebration} />}
                label="Evenement" value={eventTypeLabel(reservation?.event)}
                valueColor="rgba(244, 67, 54, 1)" isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdEvent} />}
                label="Date de l'evenement"
                value={dayjs(reservation?.event_date).format("DD-MM-YYYY")}
                isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdAccessTime} />}
                label="Heure d'arrivee artiste(s)"
                value={formatArrivalTime(reservation)} isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdSchedule} />}
                label="Fin de prestation"
                value={formatEndTime(reservation)} isMobile={isMobile}
            />
        </>
    );
}

/**
 * Renders the invoice link and pricing footer rows.
 * @param {Object} props
 * @param {Object} props.reservation - The reservation data object
 * @param {boolean} props.invoiceCopied - Whether the invoice was just copied
 * @param {Function} props.onCopyInvoice - Handler to copy the invoice
 * @param {string} props.currency - Currency symbol
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ReservationFooterRows({ reservation, invoiceCopied, onCopyInvoice, currency, isMobile }) {
    return (
        <>
            {reservation?.invoice && (
                <ContactInfoRow
                    icon={<Icon as={MdReceipt} />}
                    label="Facture"
                    value={reservation.invoice.substring(0, 10) + "..."}
                    isInvoice invoiceUrl={reservation.invoice}
                    copyable isCopied={invoiceCopied}
                    onCopy={onCopyInvoice} isMobile={isMobile}
                />
            )}
            <ContactInfoRow
                icon={<Icon as={MdPayment} />}
                label="Prix de la reservation"
                value={`${reservation?.total_amount}${currency}`}
                valueColor="rgba(76, 175, 80, 1)" isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdEvent} />}
                label="Envoyee le"
                value={dayjs(reservation?.created_at).format("DD-MM-YYYY")}
                isMobile={isMobile}
            />
        </>
    );
}

/**
 * Displays all details for a reservation including status, address,
 * event info, dates, invoice, and pricing.
 * @param {Object} props
 * @param {Object} props.reservation - The reservation data object
 * @returns {React.ReactElement}
 */
function Reservation({ reservation }) {
    const { status, countries } = useSelector((state) => state.global);
    const [invoiceCopied, setInvoiceCopied] = React.useState(false);
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const statusColors = getStatusColor(reservation?.status);
    const currency = currencyByAddress(reservation?.address, countries);

    /**
     * Copies the reservation invoice to the clipboard and shows feedback.
     * @returns {void}
     */
    const handleCopyInvoice = () => {
        if (!reservation?.invoice) return;
        navigator.clipboard.writeText(reservation.invoice);
        setInvoiceCopied(true);
        setTimeout(() => setInvoiceCopied(false), 2000);
    };

    return (
        <Box>
            <ReservationHeaderRows
                reservation={reservation} invoiceCopied={invoiceCopied}
                onCopyInvoice={handleCopyInvoice} statusColors={statusColors}
                statusLabel={status[reservation?.status]} isMobile={isMobile}
            />
            <ReservationEventRows reservation={reservation} isMobile={isMobile} />
            <ReservationFooterRows
                reservation={reservation} invoiceCopied={invoiceCopied}
                onCopyInvoice={handleCopyInvoice} currency={currency} isMobile={isMobile}
            />
        </Box>
    );
}

export default Reservation;
